import axios from 'axios';
import API from '@/config/api';
import { laporGalat, perluDilaporkan } from './lapor-galat';

/**
 * Klien HTTP terpusat.
 *
 * Sebelumnya baris `axios.defaults.baseURL = API.API_BASE_URL` diulang di
 * sekitar 20 berkas komponen, dan setiap pemanggilan menempelkan header
 * Authorization secara manual:
 *
 *   axios.get('/contingents', {
 *     headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
 *   })
 *
 * Pola itu mudah terlewat — beberapa permintaan lupa menyertakan token, dan
 * respons 401 tidak pernah ditangani sehingga pengguna terdampar di halaman
 * kosong ketika sesinya berakhir. Semua itu sekarang ditangani interceptor.
 */

const http = axios.create({
  baseURL: API.API_BASE_URL,
  timeout: 15000,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

const TOKEN_KEY = 'authToken';

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    // Abaikan bila penyimpanan diblokir.
  }
}

export function clearSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('user');
  } catch (error) {
    // Abaikan.
  }
}

// --- Permintaan keluar: sisipkan token bila ada ---------------------------
http.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Biarkan peramban menyusun boundary sendiri untuk unggahan berkas.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

// --- Respons masuk: tangani sesi berakhir & normalkan pesan galat ---------
let onUnauthorized = null;

/**
 * Dipasang di main.js supaya modul ini tidak perlu mengimpor router
 * (yang akan menimbulkan ketergantungan melingkar).
 */
export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;

    if (status === 401) {
      clearSession();
      if (onUnauthorized) onUnauthorized();
    }

    let data = error.response?.data;

    // Jika pemanggil menyetel responseType: 'blob', respons galat JSON ikut
    // dibungkus sebagai Blob oleh Axios. Bongkar isinya agar pesan asli dari
    // backend (mis. 'Berkas tidak ditemukan') tetap terbaca pengguna.
    if (data instanceof Blob && typeof data.text === 'function') {
      try {
        const teks = await data.text();
        const json = JSON.parse(teks);
        if (json && typeof json === 'object') {
          data = json;
          error.response.data = json;
        }
      } catch (_) {
        // Bukan JSON yang valid, biarkan data tetap apa adanya.
      }
    }

    /*
     * Backend kini membalas dengan bentuk seragam
     * { success, message, errors? }. Pesan diangkat ke error.friendlyMessage
     * supaya komponen tidak perlu menebak-nebak struktur respons.
     */
    const isTimeout = error.code === 'ECONNABORTED' || (error.message && error.message.toLowerCase().includes('timeout'));
    const isNetworkError = !error.response && error.message && error.message.toLowerCase().includes('network');

    error.friendlyMessage =
      data?.message ||
      data?.error ||
      (isTimeout
        ? 'Koneksi internet lambat atau terputus. Silakan periksa jaringan dan coba lagi.'
        : (isNetworkError || status === 0 || !error.response
          ? 'Tidak dapat terhubung ke server. Periksa sinyal internet Anda.'
          : 'Terjadi kesalahan. Silakan coba lagi.'));

    error.validationErrors = data?.errors || null;

    // Sama seperti instans bawaan: tidak ada kegagalan yang boleh senyap.
    if (perluDilaporkan(error)) laporGalat(error.friendlyMessage);

    return Promise.reject(error);
  }
);

export default http;
